import React, { useRef, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { CancelButton, SubmitButton } from "../../components/btns";
import { UCWords } from "../../components/resources";
import { errorAlert, successAlert } from "../../components/toastr";
import { createCategory, updateCategory } from "../../resources/api/vehicle-categories";
import { DriveTrainOptions, FuelTypes, TransmissionTypes, VehicleBodyTypes } from "../../assets/constants";



const { Control, Label } = Form;

/**
 * @param {Object} props
 * @param {"create"|"edit"} props.action
 * @param {(details: import("../../resources/api/vehicle-categories").VehicleCategoryObject) => void} props.onUpload
 * @param {{
 *  show: boolean
 *  setShow: React.Dispatch<React.SetStateAction<boolean>>
 * }} props.show
 * @param {{
 *  details: import("../../resources/api/vehicle-categories").VehicleCategoryObject
 *  setDetails: React.Dispatch<React.SetStateAction<import("../../resources/api/vehicle-categories").VehicleCategoryObject>>
 * }} props.categoryDetails
 */
const VehicleCategoryForm = ({ onUpload, action = "create", categoryDetails, show: toShow }) => {

    const inputref = useRef();

    const { show, setShow } = toShow;
    const [handlingSubmit, setHandlingSubmit] = useState(false);
    const [validated, setValidated] = useState(false);

    const { details, setDetails } = categoryDetails;



    /**
     * Handles the submission of the form
     * @param {React.MouseEvent} e 
     */
    const handleSubmit = e => {

        e.preventDefault();
        const form = inputref.current;

        if (!form.checkValidity()) {
            setValidated(true);
            errorAlert("Some errors have been detected in the form. These have been highlighted for you.", "Invalid Fields");
            return;
        }

        setHandlingSubmit(true);
        setValidated(false);

        let promise;

        if (action === "create") {
            promise = createCategory(details);
        } else {
            promise = updateCategory(details, details.id);
        }

        promise
            .then(({ category, message }) => {
                form.reset();
                successAlert(message);
                onUpload(category);
                setShow(false);
            })
            .catch(e => errorAlert(e))
            .finally(() => setHandlingSubmit(false))
    }

    return (
        <Modal show={show} onExit={() => setValidated(false)} centered animation={false} size="lg">
            <Modal.Body>
                <Form noValidate validated={validated} ref={inputref}>

                    <Row className="g-1">
                        <Col sm={6} className="my-1">
                            <Label className="form-field-title">Make</Label>
                            <Control
                                value={details.make}
                                onChange={e => setDetails({ ...details, make: e.currentTarget.value })}
                                required
                            />
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                        <Col sm={6} className="my-1">
                            <Label className="form-field-title">Model</Label>
                            <Control
                                value={details.model}
                                onChange={e => setDetails({ ...details, model: e.currentTarget.value })}
                                required
                            />
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                    </Row>


                    <Row className="g-1">
                        <Col sm={6} className="my-1">
                            <Label className="form-field-title">Min Year</Label>
                            <Control
                                value={details.min_year}
                                type="number"
                                onChange={e => setDetails({ ...details, min_year: e.currentTarget.value })}
                                required
                            />
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                        <Col sm={6} className="my-1">
                            <Label className="form-field-title">Max Year</Label>
                            <Control
                                value={details.max_year}
                                type="number"
                                min={details.min_year}
                                onChange={e => setDetails({ ...details, max_year: e.currentTarget.value })}
                                required
                            />
                            <Control.Feedback type="invalid">
                                Required & Should be higher than min year
                            </Control.Feedback>
                        </Col>
                        <Col sm={12} className="my-1">
                            <Label className="form-field-title">Description</Label>
                            <Control
                                as="textarea"
                                value={details.description}
                                onChange={e => setDetails(d => ({ ...d, description: e.target.value }))}
                                rows={1}
                                onFocus={e => (e.target.rows = 4)}
                                onBlur={e => (e.target.rows = 2)}
                                required
                            />
                            <Control.Feedback type="invalid">

                            </Control.Feedback>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} sm={4} className="my-1">
                            <Label className="form-field-title">Fuel Type</Label>
                            <Form.Select
                                value={details.fuel_type}
                                onChange={e => setDetails({ ...details, fuel_type: e.currentTarget.value })}
                                required
                            >
                                <option value="" />
                                {FuelTypes.map(t => <option key={t} value={t}>{UCWords(t.replace(/-/g, " "))}</option>)}
                            </Form.Select>
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                        <Col xs={6} sm={4} className="my-1">
                            <Label className="form-field-title">Transmission Type</Label>
                            <Form.Select
                                value={details.transmission_type}
                                onChange={e => setDetails({ ...details, transmission_type: e.currentTarget.value })}
                                required
                            >
                                <option value="" />
                                {TransmissionTypes.map(t => <option key={t} value={t}>{UCWords(t.replace(/-/g, " "))}</option>)}
                            </Form.Select>
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                        <Col xs={6} sm={4} className="my-1">
                            <Label className="form-field-title">Body Type</Label>
                            <Form.Select
                                value={details.body_type}
                                onChange={e => setDetails({ ...details, body_type: e.currentTarget.value })}
                                required
                            >
                                <option value="" />
                                {VehicleBodyTypes.map(t => <option key={t} value={t}>{UCWords(t.replace(/-/g, " "))}</option>)}
                            </Form.Select>
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                        <Col xs={6} sm={4} className="my-1">
                            <Label className="form-field-title">Drive Train</Label>
                            <Form.Select
                                value={details.drivetrain}
                                onChange={e => setDetails({ ...details, drivetrain: e.currentTarget.value })}
                                required
                            >
                                <option value="" />
                                {Object.keys(DriveTrainOptions).map(t => <option key={t} value={t}>{DriveTrainOptions[t]}</option>)}
                            </Form.Select>
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                        <Col xs={6} sm={4} className="my-1">
                            <Label className="form-field-title">Seating Capacity</Label>
                            <Control
                                value={details.seating_capacity}
                                type="number"
                                onChange={e => setDetails({ ...details, seating_capacity: e.currentTarget.value })}
                                required
                            />
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>

                    </Row>

                    <Row className="g-1">
                        <Col sm={6} className="my-1">
                            <Label className="form-field-title">Engine CC</Label>
                            <Control
                                value={details.engine_cc}
                                type="number"
                                onChange={e => setDetails({ ...details, engine_cc: e.currentTarget.value })}
                            />
                        </Col>
                        <Col sm={6} className="my-1">
                            <Label className="form-field-title">Payload Tonnes</Label>
                            <Control
                                value={details.payload_tonnes}
                                type="number"
                                onChange={e => setDetails({ ...details, payload_tonnes: e.currentTarget.value })}
                            />
                        </Col>
                    </Row>



                    <Row>
                        <Col className="mt-4 mb-0 text-end">
                            <SubmitButton isSubmitting={handlingSubmit} onClick={handleSubmit}>
                                {action === "edit" ? "Update" : "Submit"}
                            </SubmitButton>
                            <CancelButton isSubmitting={handlingSubmit} onClick={() => setShow(false)}>
                                Cancel
                            </CancelButton>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
        </Modal >
    )
}



export default VehicleCategoryForm;